import {clearAllWorkflowDrafts} from '@app/modules/workflows/workflow-wizard/workflow-draft.util';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@src/environments/environment';
import { isPlatformBrowser } from '@angular/common';
import {BehaviorSubject, from, Observable, of} from 'rxjs';
import { tap, catchError, concatMap } from 'rxjs/operators';
import { Auth, SingleResult } from '../interfaces';
import { AuthRegisterCredentials } from '@app/services/authRegisterCredentials';
import { AuthCredentials } from '@app/services/authCredentials';
import { isBrowser } from '@app/util/is-browser';
import { KeycloakAuthResponse, KeycloakLogoutResponse, KeycloakRefreshResponse } from '../interfaces/entities/keycloak-auth';
import { FirebaseService } from '@app/services/firebase.service';
import { BrandService } from '@app/services/brand.service';
import { NotificationApiService } from '@app/services/api/notification-api.service';
import { Brand } from '../shared/models/brand.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private static AUTH_KEY = 'auth';
  private static SUPER_ADMIN_TAKE_CONTROL_ACCOUNT_UUID = 'control_account_uuid';
  private static KEYCLOAK_AUTH_KEY = 'keycloak_auth';

  private authChangesSubject = new BehaviorSubject<Auth | null>(null);
  private authControlAccountChangeSubject = new BehaviorSubject<{ name: string; accountUuid: string } | null>(null);
  private invalidateConfigChanges = new BehaviorSubject<any>(null);

  private authControlAccountChangeChannel?: BroadcastChannel;
  private invalidateConfigChannel?: BroadcastChannel;

  private tabId = Math.random().toString(36);

  constructor(
    private http: HttpClient,
    private firebaseService: FirebaseService,
    private brandService: BrandService,
    private notificationApiService: NotificationApiService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.authControlAccountChangeChannel = new BroadcastChannel('authControlChangeChannel');
      this.invalidateConfigChannel = new BroadcastChannel('invalidateConfigChannel');
    }

    this.authChangesSubject.next(this.getAuth());
    this.setupCrossTabsSync();
    this.crossTabsInvalidateConfig();
    this.onPageLoadAuthControlInit();
  }

  getAuthChanges(): Observable<Auth | null> {
    return this.authChangesSubject;
  }

  getAccountControlChanges() {
    return this.authControlAccountChangeSubject;
  }

  getInvalidateConfigChanges() {
    return this.invalidateConfigChanges;
  }

  login(credentials: AuthCredentials) {
    return this.http.post(`${environment.apiUrl}/api/users/login`, credentials)
      .pipe(tap((result: SingleResult<Auth>) => {
        this.storeAuth(result.data);
      }));
  }

  send2FAEmail(credentials: { email: string, password: string }) {
    return this.http.post<{ smsAvailable: boolean }>(`${environment.apiUrl}/api/users/2fa/send-token`, credentials);
  }

  resend2FA(data: { email: string, channel: 'email' | 'sms' }) {
    return this.http.post(`${environment.apiUrl}/api/users/2fa/resend`, data);
  }

  registerDemo(credentials: AuthRegisterCredentials) {
    return this.http.post<{ success: boolean, error_code?: string; keycloak_refresh_token?: string; }>(`${environment.apiUrl}/api/demo/register`, credentials);
  }

  sendResetPasswordLink(credentials: { email: string }) {
    return this.http.post(`${environment.apiUrl}/api/password/send-reset-link`, credentials);
  }

  resetPassword(credentials: { email: string; token: string; password: string }) {
    return this.http.post(`${environment.apiUrl}/api/password/reset-password`, credentials);
  }

  // Keycloak authentication methods
  keycloakLogin() {
    // Redirect to backend Keycloak endpoint
    if (isPlatformBrowser(this.platformId)) {
      window.location.href = `${environment.apiUrl}/api/auth/keycloak`;
    }
  }

  keycloakCallback(params: { code?: string; state?: string; error?: string; error_description?: string }) {
    const queryParams = new URLSearchParams();
    if (params.code) queryParams.set('code', params.code);
    if (params.state) queryParams.set('state', params.state);
    if (params.error) queryParams.set('error', params.error);
    if (params.error_description) queryParams.set('error_description', params.error_description);

    const queryString = queryParams.toString();
    const url = `${environment.apiUrl}/api/auth/keycloak/callback${queryString ? '?' + queryString : ''}`;

    return this.http.get<KeycloakAuthResponse>(url).pipe(
      tap((result: KeycloakAuthResponse) => {
        this.storeKeycloakAuth(result);
      })
    );
  }

  keycloakLogout({skipExternalRedirect = false} = {}) {
    return from(this.firebaseService.unsubscribe()).pipe(
      concatMap(() => this.http.post<KeycloakLogoutResponse>(`${environment.apiUrl}/api/auth/keycloak/logout`, {})),
      tap((result: KeycloakLogoutResponse) => {
        this.clearKeycloakAuth();
        if (!skipExternalRedirect && isPlatformBrowser(this.platformId)) {
          window.location.href = result.logout_url;
        }
      }),
      catchError(() => {
        this.clearKeycloakAuth();
        return of(null);
      })
    );
  }

  keycloakGetUserInfo() {
    return this.http.get(`${environment.apiUrl}/api/auth/keycloak/userinfo`);
  }

  keycloakRefreshToken(refreshToken: string) {
    return this.http.post<KeycloakRefreshResponse>(`${environment.apiUrl}/api/auth/keycloak/refresh`, {
      refresh_token: refreshToken
    }).pipe(
      tap((result: KeycloakRefreshResponse) => {
        this.updateKeycloakToken(result);
      })
    );
  }

  /**
   * Authenticate with Keycloak refresh token from mobile app
   * The refresh token will be invalidated after authentication
   */
  authenticateWithKeycloakToken(refreshToken: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/api/auth/mobile/keycloak-token`, {
      refresh_token: refreshToken
    }).pipe(
      tap((result: SingleResult<Auth>) => {
        this.storeAuth(result.data);
      })
    );
  }

  isKeycloakAuthenticated(): boolean {
    return this.getKeycloakAuth() !== null;
  }

  getKeycloakToken(): string | null {
    const keycloakAuth = this.getKeycloakAuth();
    return keycloakAuth?.token || null;
  }

  getKeycloakAuth(): KeycloakAuthResponse | null {
    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem(AuthService.KEYCLOAK_AUTH_KEY);
      return stored ? JSON.parse(stored) : null;
    }
    return null;
  }

  private storeKeycloakAuth(auth: KeycloakAuthResponse) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    localStorage.setItem(AuthService.KEYCLOAK_AUTH_KEY, JSON.stringify(auth));
    this.authChangesSubject.next(this.convertKeycloakToAuth(auth));
  }

  private clearKeycloakAuth() {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(AuthService.KEYCLOAK_AUTH_KEY);
    }
    this.notificationApiService.stopPolling();
    this.authChangesSubject.next(null);
  }

  private updateKeycloakToken(refreshResponse: KeycloakRefreshResponse) {
    const currentAuth = this.getKeycloakAuth();
    if (currentAuth) {
      const updatedAuth: KeycloakAuthResponse = {
        ...currentAuth,
        token: refreshResponse.token,
        refresh_token: refreshResponse.refresh_token || currentAuth.refresh_token,
        expires_in: refreshResponse.expires_in || currentAuth.expires_in
      };
      this.storeKeycloakAuth(updatedAuth);
    }
  }

  private convertKeycloakToAuth(keycloakAuth: KeycloakAuthResponse): Auth {
    return {
      userUuid: keycloakAuth.user.uuid,
      accountUuid: keycloakAuth.user.uuid, // Assuming same for Keycloak
      email: keycloakAuth.user.email,
      isDemo: false,
      isAdmin: false, // This would need to be determined from Keycloak roles
      locale: 'en', // Default locale, could be extracted from Keycloak
      token: keycloakAuth.token,
      isDeactivated: false
    };
  }

  logout() {
    // Check if user is authenticated via Keycloak
    if (this.isKeycloakAuthenticated()) {
      return this.keycloakLogout();
    }

    // Regular logout
    return from(this.firebaseService.unsubscribe()).pipe(
      concatMap(() => this.http.post(`${environment.apiUrl}/api/users/logout`, {})),
      tap(() => {
        this.clearAuthLocally();
      }),
      catchError(() => {
        this.clearAuthLocally();
        return of(null);
      })
    );
  }

  /**
   * Clear authentication locally without making HTTP call
   * Use this when token is already invalid (e.g., on 401 errors)
   * Note: Does not call Firebase unsubscribe since token is already invalid
   */
  clearAuthLocally() {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(AuthService.AUTH_KEY);
      localStorage.removeItem(AuthService.KEYCLOAK_AUTH_KEY);
      localStorage.removeItem(AuthService.SUPER_ADMIN_TAKE_CONTROL_ACCOUNT_UUID);
      clearAllWorkflowDrafts();
    }
    this.notificationApiService.stopPolling();
    this.authChangesSubject.next(null);
  }

  seedDemo(type: number) {
    return this.http.post(`${environment.apiUrl}/api/demo`, { type });
  }

  isLoggedIn(): boolean {
    return this.getAuth() !== null || this.isKeycloakAuthenticated();
  }

  getToken() {
    if (this.isLoggedIn()) {
      // Check Keycloak first, then regular auth
      const keycloakToken = this.getKeycloakToken();
      if (keycloakToken) {
        return keycloakToken;
      }
      return this.getAuth()?.token || null;
    }

    return null;
  }

  getAuth(): Auth | null {
    if (isPlatformBrowser(this.platformId)) {
      return JSON.parse(localStorage.getItem(AuthService.AUTH_KEY));
    }
    return null;
  }

  getAccountControl(): { name: string; accountUuid: string } | null {
    if (isPlatformBrowser(this.platformId)) {
      return JSON.parse(localStorage.getItem(AuthService.SUPER_ADMIN_TAKE_CONTROL_ACCOUNT_UUID));
    }

    return null;
  }

  public setupCookieForEasierImageLoading() {
    const apiUrl = this.brandService.current === 'carpeak'
      ? ((environment as { carpeakApiUrl?: string }).carpeakApiUrl ?? environment.apiUrl)
      : environment.apiUrl;
    return this.http.get(`${apiUrl}/api/users/me/cookie`, { withCredentials: true });
  }

  public setAuth(auth: Auth) {
    this.storeAuth(auth);
  }

  private storeAuth(auth: Auth) {
    if (!isPlatformBrowser(this.platformId) || auth.isDeactivated) {
      return;
    }

    if (auth.brand) {
      this.brandService.setBrand(auth.brand as Brand);
    }

    localStorage.setItem(AuthService.AUTH_KEY, JSON.stringify(auth));
    this.authChangesSubject.next(auth);
  }

  isAdmin() {
    if (this.isLoggedIn()) {
      return !!this.getAuth()?.isAdmin;
    }
    return false;
  }

  takeControl(control: { name: string; accountUuid: string } | null) {
    if (isPlatformBrowser(this.platformId)) {

      if (control) {
        localStorage.setItem(AuthService.SUPER_ADMIN_TAKE_CONTROL_ACCOUNT_UUID, JSON.stringify(control));
      } else {
        localStorage.removeItem(AuthService.SUPER_ADMIN_TAKE_CONTROL_ACCOUNT_UUID);
      }

      if (isBrowser()) {
        window.location.reload();
      }

      this.authControlAccountChangeSubject.next(control);

      // Notify other tabs
      this.authControlAccountChangeChannel?.postMessage({
        control,
        tabId: this.tabId
      });
    }
  }

  invalidateConfigOnAllTabs(eventKey: string) {
    if (isPlatformBrowser(this.platformId)) {
      this.invalidateConfigChannel.postMessage({
        eventKey,
        tabId: this.tabId
      });
    }
  }

  private crossTabsInvalidateConfig() {
    if (isPlatformBrowser(this.platformId)) {
      this.invalidateConfigChannel.onmessage = (ev) => {
        // Prevent loops
        if (ev.data.tabId && ev.data.tabId === this.tabId) {
          return;
        }

        this.getInvalidateConfigChanges();
        if (isBrowser()) {
          window.location.reload();
        }
      }
    }
  }

  private setupCrossTabsSync() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.authControlAccountChangeChannel.onmessage = (ev) => {
      // Prevent loops
      if (ev.data.tabId && ev.data.tabId === this.tabId) {
        return;
      }

      console.warn('Received control change', ev.data);

      // if (confirm(`Received change of Super Admin control, accept it?`)) {
      //   this.authControlAccountChangeSubject.next(ev.data.control);
      //   window.location.reload();
      // }
    };
  }

  private onPageLoadAuthControlInit() {
    const accountControl = this.getAccountControl();
    if (accountControl) {
      if (environment.production && confirm(`You are currently controlling an account: \n >> ${accountControl.name}.\n Do you want to CONTINUE?`)) {
        this.authControlAccountChangeSubject.next(this.getAccountControl());
      } else if (!environment.production) {
        this.authControlAccountChangeSubject.next(this.getAccountControl());
      } else {
        this.takeControl(null);
      }
    }
  }
}
